import { useEffect, useRef, useState } from 'react'
import { Check, ClipboardList, FolderOpen, GitBranch, MapPin, Search, X } from 'lucide-react'

import { Input } from 'components/ui/input'
import { cn } from 'lib/utils'

import * as presentationService from '../services/presentationService'
import type { AssetOption, PresentationScope } from '../types/presentation.types'

/**
 * Choose one of the user's assets (MYD-494). Used for a new deck's first story,
 * and built to be reused by the editor's element picker (MYD-495).
 *
 * Search is server-side (`GET /presentations/assets`), debounced, and scoped
 * to what the user can open, so every option offered will actually resolve.
 */

export const SCOPE_OPTIONS: ReadonlyArray<{ scope: PresentationScope; label: string; icon: typeof MapPin }> = [
	{ scope: 'location', label: 'Location', icon: MapPin },
	{ scope: 'project', label: 'Project', icon: GitBranch },
	{ scope: 'portfolio', label: 'Portfolio', icon: FolderOpen },
	{ scope: 'evaluation', label: 'Evaluation', icon: ClipboardList },
]

/** Long enough to skip a request per keystroke, short enough to feel live. */
const DEBOUNCE_MS = 250

interface AssetPickerProps {
	value: AssetOption | null
	onChange: (asset: AssetOption | null) => void
	/** Limit the asset types offered. Defaults to all four. */
	scopes?: PresentationScope[]
	className?: string
}

export function AssetPicker({ value, onChange, scopes, className }: AssetPickerProps) {
	const options = SCOPE_OPTIONS.filter(option => !scopes || scopes.includes(option.scope))
	const [scope, setScope] = useState<PresentationScope>(value?.scope ?? options[0]?.scope ?? 'location')
	const [query, setQuery] = useState('')
	const [results, setResults] = useState<AssetOption[]>([])
	const [loading, setLoading] = useState(false)
	const [failed, setFailed] = useState(false)

	// Only the latest request may write results. Without this, a slow reply for
	// "hol" could land after the reply for "hollister" and replace it.
	const latest = useRef(0)

	useEffect(() => {
		const request = ++latest.current
		setLoading(true)
		setFailed(false)

		const timer = setTimeout(() => {
			presentationService
				.searchAssets(scope, query)
				.then(rows => {
					if (request === latest.current) setResults(rows)
				})
				.catch(() => {
					if (request === latest.current) {
						setResults([])
						setFailed(true)
					}
				})
				.finally(() => {
					if (request === latest.current) setLoading(false)
				})
		}, DEBOUNCE_MS)

		return () => clearTimeout(timer)
	}, [scope, query])

	const scopeLabel = options.find(option => option.scope === scope)?.label.toLowerCase() ?? 'asset'

	return (
		<div className={cn('space-y-3', className)}>
			<div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Asset type">
				{options.map(({ scope: optionScope, label, icon: Icon }) => (
					<button
						key={optionScope}
						type="button"
						role="radio"
						aria-checked={scope === optionScope}
						onClick={() => setScope(optionScope)}
						className={cn(
							'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
							scope === optionScope
								? 'border-primary bg-primary/5 text-primary'
								: 'border-gray-200 text-gray-600 hover:bg-gray-50',
						)}
					>
						<Icon className="w-3.5 h-3.5" />
						{label}
					</button>
				))}
			</div>

			<div className="relative">
				<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
				<Input
					value={query}
					onChange={event => setQuery(event.target.value)}
					placeholder={`Search your ${scopeLabel}s`}
					aria-label={`Search ${scopeLabel}s`}
					className="pl-8"
				/>
			</div>

			<div className="max-h-56 overflow-y-auto rounded-md border border-gray-200" role="listbox" aria-label="Assets">
				{loading && results.length === 0 ? (
					<p className="px-3 py-4 text-sm text-muted-foreground">Searching...</p>
				) : failed ? (
					<p className="px-3 py-4 text-sm text-destructive">Could not load your {scopeLabel}s. Try again.</p>
				) : results.length === 0 ? (
					<p className="px-3 py-4 text-sm text-muted-foreground">
						{query ? `No ${scopeLabel}s match "${query}".` : `You have no ${scopeLabel}s yet.`}
					</p>
				) : (
					results.map(asset => {
						const selected = value?.scope === asset.scope && value.entityId === asset.entityId
						return (
							<button
								key={`${asset.scope}:${asset.entityId}`}
								type="button"
								role="option"
								aria-selected={selected}
								onClick={() => onChange(selected ? null : asset)}
								className={cn(
									'flex w-full items-center justify-between gap-3 px-3 py-2 text-left border-b border-gray-100 last:border-b-0 transition-colors',
									selected ? 'bg-primary/5' : 'hover:bg-gray-50',
								)}
							>
								<span className="min-w-0">
									<span className="block truncate text-sm font-medium text-gray-900">{asset.name || 'Untitled'}</span>
									{asset.subtitle && <span className="block truncate text-xs text-gray-500">{asset.subtitle}</span>}
								</span>
								{selected && <Check className="w-4 h-4 flex-shrink-0 text-primary" />}
							</button>
						)
					})
				)}
			</div>

			{value && (
				<div className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm">
					<span className="truncate">
						<span className="text-muted-foreground">Selected: </span>
						<span className="font-medium text-gray-900">{value.name}</span>
					</span>
					<button
						type="button"
						onClick={() => onChange(null)}
						className="ml-2 rounded p-0.5 text-muted-foreground hover:text-gray-900"
						aria-label="Clear selection"
					>
						<X className="w-4 h-4" />
					</button>
				</div>
			)}
		</div>
	)
}

export default AssetPicker
